import type { Grade } from '../types';

export interface PendingReport {
  id: string;
  grade: Grade;
  latitude: number;
  longitude: number;
  timestamp: string;
  street_name: string;
  local_authority: string;
  device_id: string | null;
  photo_blob: Blob | null;
  status: 'pending' | 'syncing' | 'failed';
  retry_count: number;
  created_at: string;
  litter_left_side?: boolean;
  litter_right_side?: boolean;
  litter_central_reservation?: boolean;
  dangerous_debris?: boolean;
  registration_plate_detected?: boolean;
}

const DB_NAME = 'RoadVlogOfflineDB';
const DB_VERSION = 1;
const STORE_NAME = 'pendingReports';

class OfflineStorage {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          objectStore.createIndex('status', 'status', { unique: false });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async addReport(report: PendingReport): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(report);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAllReports(): Promise<PendingReport[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingReports(): Promise<PendingReport[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('status');
      const request = index.getAll('pending');

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updateReportStatus(id: string, status: 'pending' | 'syncing' | 'failed', retryCount?: number): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const report = getRequest.result;
        if (report) {
          report.status = status;
          if (retryCount !== undefined) {
            report.retry_count = retryCount;
          }
          const updateRequest = store.put(report);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve();
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async deleteReport(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearOldFailedReports(daysOld: number = 7): Promise<void> {
    if (!this.db) await this.init();

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    const allReports = await this.getAllReports();
    const toDelete = allReports.filter(
      report => report.status === 'failed' &&
                new Date(report.created_at) < cutoffDate
    );

    for (const report of toDelete) {
      await this.deleteReport(report.id);
    }
  }

  async getPendingCount(): Promise<number> {
    const pending = await getPendingReports();
    return pending.length;
  }
}

export const offlineStorage = new OfflineStorage();

export async function initOfflineStorage(): Promise<void> {
  await offlineStorage.init();
}

export async function savePendingReport(report: Omit<PendingReport, 'id' | 'status' | 'retry_count' | 'created_at'>): Promise<void> {
  const pendingReport: PendingReport = {
    ...report,
    id: `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    status: 'pending',
    retry_count: 0,
    created_at: new Date().toISOString(),
  };

  await offlineStorage.addReport(pendingReport);
}

export async function getPendingReports(): Promise<PendingReport[]> {
  return offlineStorage.getPendingReports();
}

export async function getAllPendingReports(): Promise<PendingReport[]> {
  return offlineStorage.getAllReports();
}

export async function deletePendingReport(id: string): Promise<void> {
  await offlineStorage.deleteReport(id);
}

export async function updatePendingReportStatus(id: string, status: 'pending' | 'syncing' | 'failed', retryCount?: number): Promise<void> {
  await offlineStorage.updateReportStatus(id, status, retryCount);
}
